import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { subscribeGames, deleteGame, triggerRun } from '../services/games';
import type { GameSettings, SupportedLanguage } from '../types';
import { ALL_LANGUAGES, LANGUAGE_LABELS, intervalLabel } from '../types';

type SortKey = 'title' | 'maxKeywords' | 'intervalDays' | 'lastRunAt' | 'enabled';
type SortDir = 'asc' | 'desc';
type StatusFilter = 'all' | 'enabled' | 'disabled';

function formatDate(value: GameSettings['lastRunAt']): string {
  if (!value) return '未実行';
  if (value instanceof Date) return value.toLocaleString('ja-JP');
  return new Date(value.seconds * 1000).toLocaleString('ja-JP');
}

/** Epoch ms of the last run; never-run games sort as oldest. */
function lastRunMillis(value: GameSettings['lastRunAt']): number {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  return value.seconds * 1000;
}

function getSortValue(g: GameSettings, key: SortKey): number | string {
  switch (key) {
    case 'title': return g.title;
    case 'maxKeywords': return g.maxKeywords;
    case 'intervalDays': return g.intervalDays ?? 1;
    case 'lastRunAt': return lastRunMillis(g.lastRunAt ?? null);
    case 'enabled': return g.enabled ? 1 : 0;
  }
}

export function TopPage() {
  const [games, setGames] = useState<GameSettings[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [langFilter, setLangFilter] = useState<SupportedLanguage | ''>('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'title', dir: 'asc' });
  const navigate = useNavigate();

  // Fire-and-forget: ask once, kick the callable, immediately tell the user it
  // started. Errors during the long-running pipeline surface in Cloud Functions
  // logs; we only alert on the request-time failure.
  const runNow = (g: GameSettings) => {
    const ok = confirm(
      `「${g.title}」を今すぐ実行しますか？\n外部API（Serper / Gemini）を呼び出すため数分かかり、API利用料金が発生します。`
    );
    if (!ok) return;
    triggerRun(g.id).catch((err) => {
      console.error('triggerRun failed', err);
    });
    alert('実行を開始しました。完了後、ページを再読み込みすると最新の結果が表示されます。');
  };

  useEffect(() => subscribeGames(setGames), []);

  const visible = useMemo(() => {
    if (!games) return [];
    const q = query.trim().toLowerCase();
    const filtered = games.filter((g) => {
      if (statusFilter === 'enabled' && !g.enabled) return false;
      if (statusFilter === 'disabled' && g.enabled) return false;
      if (langFilter && !g.languages.includes(langFilter)) return false;
      if (q && !g.title.toLowerCase().includes(q)) return false;
      return true;
    });
    filtered.sort((a, b) => {
      const va = getSortValue(a, sort.key);
      const vb = getSortValue(b, sort.key);
      const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), 'ja');
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return filtered;
  }, [games, statusFilter, langFilter, query, sort]);

  const sortBy = (key: SortKey) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'title' ? 'asc' : 'desc' }));
  };

  const arrow = (key: SortKey) => (sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '');

  if (games === null) return <div>読み込み中…</div>;

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>ゲーム一覧</h2>
        <button className="primary" onClick={() => navigate('/games/new')}>+ ゲーム追加</button>
      </div>

      {games.length === 0 ? (
        <div className="card empty">まだゲームが登録されていません。「+ ゲーム追加」から作成してください。</div>
      ) : (
        <>
        <div className="row list-filters">
          <input
            type="search"
            placeholder="タイトルで絞り込み"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
            <option value="all">状態: すべて</option>
            <option value="enabled">状態: 有効</option>
            <option value="disabled">状態: 無効</option>
          </select>
          <select value={langFilter} onChange={(e) => setLangFilter(e.target.value as SupportedLanguage | '')}>
            <option value="">言語: すべて</option>
            {ALL_LANGUAGES.map((l) => (
              <option key={l} value={l}>{LANGUAGE_LABELS[l]}</option>
            ))}
          </select>
          <span className="muted">{visible.length} / {games.length} 件</span>
        </div>
        <table>
          <thead>
            <tr>
              <th onClick={() => sortBy('title')}>タイトル{arrow('title')}</th>
              <th>言語</th>
              <th onClick={() => sortBy('maxKeywords')}>取得する最大キーワード数{arrow('maxKeywords')}</th>
              <th onClick={() => sortBy('intervalDays')}>更新頻度{arrow('intervalDays')}</th>
              <th onClick={() => sortBy('lastRunAt')}>最終実行{arrow('lastRunAt')}</th>
              <th onClick={() => sortBy('enabled')}>状態{arrow('enabled')}</th>
              <th style={{ width: 280 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={7} className="empty">条件に一致するゲームはありません。</td>
              </tr>
            )}
            {visible.map((g) => (
              <tr key={g.id}>
                <td>
                  <Link to={`/games/${g.id}`}>{g.title}</Link>
                </td>
                <td>{g.languages.map((l) => <span key={l} className="tag">{LANGUAGE_LABELS[l]}</span>)}</td>
                <td>{g.maxKeywords}</td>
                <td>{intervalLabel(g.intervalDays ?? 1)}</td>
                <td className="muted">{formatDate(g.lastRunAt ?? null)}</td>
                <td>
                  <span className={`status-badge ${g.enabled ? 'status-on' : 'status-off'}`}>
                    {g.enabled ? '有効' : '無効'}
                  </span>
                </td>
                <td>
                  <div className="row" style={{ gap: 6 }}>
                    <button className="secondary" onClick={() => navigate(`/games/${g.id}/edit`)}>設定</button>
                    <button className="secondary" onClick={() => runNow(g)}>
                      今すぐ実行
                    </button>
                    <button
                      className="danger"
                      onClick={async () => {
                        if (!confirm(`「${g.title}」を削除しますか？`)) return;
                        await deleteGame(g.id);
                      }}
                    >
                      削除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}
    </>
  );
}
